import * as React from "react";
import { Prompt } from "react-router-dom";

import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormField,
  Input,
  RadioGroup,
  type RadioOption,
  Switch,
  Textarea,
} from "Components/ui";
import { ChevronDown, ChevronUp } from "Components/ui/icons";
import { Enforcement, TenantBilling } from "api/billing";
import { TenantLinkStatus } from "api/teleopdLink";
import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { useAccountActions, useTeleopdRoles } from "../data";
import {
  AudienceRole,
  buildAccountUpdate,
  formFingerprint,
  formFromAccount,
  hasRuleErrors,
  readOnlyBlockers,
  relaxedRules,
  reminderChannelsLabel,
  RulesForm,
  SavedRules,
  validateRules,
} from "../rules";
import AudiencePicker from "./AudiencePicker";
import ContactsEditor from "./ContactsEditor";
import ReminderOffsetsEditor from "./ReminderOffsetsEditor";

interface RulesTabProps {
  tenantId: string;
  billing: TenantBilling;
  link: TenantLinkStatus | undefined;
  canWrite: boolean;
  /** billing/approve: needed (with a reason) for changes that relax enforcement. */
  canApprove: boolean;
}

const ENFORCEMENT_OPTIONS: Array<{ id: Enforcement; label: string; description: string }> = [
  {
    id: "off",
    label: "Off",
    description: "Track invoices and send reminders only. TeleOpd shows nothing.",
  },
  {
    id: "warn_only",
    label: "Warn only",
    description:
      "The roles below see a payment banner from the warning date. Nothing is ever blocked.",
  },
  {
    id: "read_only",
    label: "Warn, then read-only",
    description:
      "After the warning, staff can view but not change data until paid. Patient-facing flows and logins keep working.",
  },
];

/** Enforcement, warning audience, reminders, contacts and advanced prefixes for one client. */
const RulesTab: React.FC<RulesTabProps> = ({ tenantId, billing, link, canWrite, canApprove }) => {
  const { account, schedules } = billing;
  // Re-derive the saved form only when the saved settings change: react-query's structural sharing
  // keeps unchanged data referentially stable across refetches, so unsaved edits survive them.
  const { contacts, policy, reminders } = account;
  const saved = React.useMemo(
    () => formFromAccount({ contacts, policy, reminders } as SavedRules),
    [contacts, policy, reminders]
  );
  const [form, setForm] = React.useState<RulesForm>(saved);
  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState<string>("");
  const [advancedOpen, setAdvancedOpen] = React.useState<boolean>(
    account.policy.extraWritablePrefixes.length > 0
  );
  const actions = useAccountActions(tenantId);

  const linked = Boolean(link && link.linked);
  const rolesQuery = useTeleopdRoles(tenantId, linked);

  React.useEffect(() => {
    setForm(saved);
  }, [saved]);

  // With no audience chosen yet, TeleOpd's full-access roles are offered as a one-click
  // suggestion. They are never ticked silently: saving another rule must not change who sees
  // billing warnings.
  const suggestedAudience: AudienceRole[] =
    canWrite && saved.audience.length === 0 && form.audience.length === 0 && rolesQuery.data
      ? rolesQuery.data.roles
          .filter((role) => role.fullAccess)
          .map((role) => ({ id: role.id, name: role.name }))
      : [];

  const errors = validateRules(form);
  const invalid = hasRuleErrors(errors);
  const dirty = formFingerprint(form) !== formFingerprint(saved);
  const relaxed = relaxedRules(saved, form);
  const needsReason = relaxed.length > 0;
  const blockedRelaxing = needsReason && !canApprove;
  const blockers = readOnlyBlockers(link);
  const readOnlyAllowed = blockers.length === 0;
  const warningDays = Number(form.warningDays);
  const lateSchedules = errors.warningDays
    ? []
    : schedules.filter(
        (schedule) => schedule.status !== "ended" && schedule.issueDaysBefore < warningDays
      );

  const patch = (changes: Partial<RulesForm>) => setForm((current) => ({ ...current, ...changes }));

  const handleSave = async () => {
    setShowErrors(true);
    setSubmitError(null);
    if (invalid || blockedRelaxing || (needsReason && !reason.trim())) {
      return;
    }
    setSaving(true);
    try {
      await actions.update.mutateAsync(
        needsReason
          ? { ...buildAccountUpdate(form), reason: reason.trim() }
          : buildAccountUpdate(form)
      );
      setReason("");
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canWrite || saving;

  const enforcementOptions: RadioOption<Enforcement>[] = ENFORCEMENT_OPTIONS.map((option) => {
    const blocked = option.id === "read_only" && !readOnlyAllowed;
    const checked = form.enforcement === option.id;
    return {
      value: option.id,
      label: option.label,
      disabled: blocked && !checked,
      description: (
        <>
          {option.description}
          {blocked && (
            <span className="mt-1 block text-xs text-warning-text">
              {checked ? "Saved, but TeleOpd can't enforce it right now: " : "Not available: "}
              {blockers.join(" ")}
            </span>
          )}
        </>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <Prompt
        when={dirty && canWrite}
        message="You have unsaved billing rule changes. Leave without saving?"
      />
      {!canWrite && (
        <Alert tone="neutral">
          You can view these rules. Changing them needs billing write access.
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Enforcement"
          description="What TeleOpd does when this client doesn't pay on time."
        />
        <CardBody>
          <RadioGroup
            aria-label="Enforcement"
            options={enforcementOptions}
            value={form.enforcement}
            onValueChange={(enforcement) => patch({ enforcement })}
            disabled={disabled}
          />

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Warning days"
              id="rules-warning-days"
              error={errors.warningDays || undefined}
              hint="How many days before the due date TeleOpd starts warning (3–60)."
            >
              <Input
                type="text"
                inputMode="numeric"
                value={form.warningDays}
                disabled={disabled}
                onChange={(event) => patch({ warningDays: event.target.value })}
                className="tabular-nums"
              />
            </FormField>
            <FormField
              label="Grace days"
              id="rules-grace-days"
              error={errors.graceDays || undefined}
              hint="Extra days after the due date before read-only starts, at 10:00 IST (0–60)."
            >
              <Input
                type="text"
                inputMode="numeric"
                value={form.graceDays}
                disabled={disabled}
                onChange={(event) => patch({ graceDays: event.target.value })}
                className="tabular-nums"
              />
            </FormField>
          </div>
          {lateSchedules.length > 0 && (
            <Alert tone="warning" className="mt-4">
              {lateSchedules.map((schedule) => `“${schedule.name}”`).join(", ")}{" "}
              {lateSchedules.length === 1 ? "issues its invoices" : "issue their invoices"} fewer
              than {warningDays} days before the due date. Edit{" "}
              {lateSchedules.length === 1 ? "it" : "them"} on the Charges tab so the invoice exists
              before warnings start.
            </Alert>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Who sees warnings"
          description="TeleOpd roles that see the amount and a Pay now button. Other staff only see the generic read-only notice."
        />
        <CardBody>
          <AudiencePicker
            linked={linked}
            roles={rolesQuery.data}
            loading={rolesQuery.isLoading && linked}
            fetching={rolesQuery.isFetching}
            error={rolesQuery.error}
            selected={form.audience}
            suggested={suggestedAudience}
            disabled={disabled}
            onChange={(audience) => patch({ audience })}
            onRefresh={() => {
              rolesQuery.refetch();
            }}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Reminders"
          description={
            reminderChannelsLabel(billing.notifications)
              ? `Sends the pay link to the billing contacts by ${reminderChannelsLabel(
                  billing.notifications
                )}, on the days below, between 9 am and 8 pm IST.`
              : "Neither email nor WhatsApp reminders are set up yet, so nothing is sent for now."
          }
        />
        <CardBody className="space-y-5">
          <div className="flex items-start gap-3">
            <Switch
              checked={form.remindersEnabled}
              onCheckedChange={(enabled) => patch({ remindersEnabled: enabled })}
              disabled={disabled}
              aria-label="Send reminders"
              className="mt-0.5"
            />
            <div>
              <p className="text-sm font-semibold text-foreground">
                {form.remindersEnabled ? "Reminders on" : "Reminders off"}
              </p>
              <p className="text-sm text-muted-foreground">
                SAP sends the latest due reminder for each open invoice; manual reminders always
                work.
              </p>
            </div>
          </div>
          <div className={form.remindersEnabled ? undefined : "opacity-60"}>
            <p className="mb-2 text-sm font-medium text-foreground">
              Days relative to the due date
            </p>
            <ReminderOffsetsEditor
              offsets={form.offsets}
              onChange={(offsets) => patch({ offsets })}
              disabled={disabled}
            />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Billing contacts</p>
            <ContactsEditor
              contacts={form.contacts}
              errors={showErrors ? errors.contacts : {}}
              listError={errors.contactsList}
              disabled={disabled}
              onChange={(contacts) => patch({ contacts })}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <Button
          variant="ghost"
          fullWidth
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
          aria-controls="rules-advanced"
          className={cn(
            "h-auto justify-between gap-4 whitespace-normal rounded-card px-5 py-4 text-left",
            advancedOpen && "rounded-b-none"
          )}
        >
          <span className="min-w-0">
            <span className="block text-base font-semibold text-foreground">Advanced</span>
            <span className="block text-sm font-normal text-muted-foreground">
              Extra paths TeleOpd keeps writable while read-only.
            </span>
          </span>
          <span className="text-muted-foreground">
            {advancedOpen ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          </span>
        </Button>
        {advancedOpen && (
          <div id="rules-advanced" className="border-t border-border px-5 py-4">
            <FormField
              label="Extra writable path prefixes"
              id="rules-prefixes"
              hint="One per line, starting with /. Matched with “starts with”, never as a pattern. Up to 20."
            >
              <Textarea
                rows={4}
                value={form.prefixesText}
                disabled={disabled}
                spellCheck={false}
                placeholder="/api/module/some-path/"
                aria-invalid={errors.prefixes.length > 0 ? true : undefined}
                onChange={(event) => patch({ prefixesText: event.target.value })}
                className="font-mono text-xs"
              />
            </FormField>
            {errors.prefixes.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-danger-text">
                {errors.prefixes.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      {submitError && (
        <Alert tone="danger" title="Not saved">
          {submitError}
        </Alert>
      )}

      {canWrite && (dirty || saving) && (
        // Sticky inside the content column: a fixed full-width bar slides under the app sidebar.
        <div className="sticky bottom-4 z-30 rounded-card border border-border bg-surface-raised px-4 py-3 shadow-popover">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex flex-wrap items-center gap-x-2 text-sm text-foreground">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
              Unsaved changes
              {showErrors && invalid && (
                <span className="text-danger-text">Fix the highlighted fields first.</span>
              )}
            </p>
            {needsReason && (
              <div className="w-full">
                {blockedRelaxing ? (
                  <p className="text-sm text-danger-text">
                    Relaxing enforcement ({relaxed.join(", ")}) needs billing approve access.
                  </p>
                ) : (
                  <FormField
                    label={`Reason for relaxing enforcement (${relaxed.join(", ")})`}
                    id="rules-reason"
                    required
                    error={
                      showErrors && !reason.trim()
                        ? "Add a reason; it goes to the audit log."
                        : undefined
                    }
                  >
                    <Input
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      maxLength={500}
                      placeholder="e.g. Agreed with the client's finance head on 28 Oct"
                    />
                  </FormField>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setForm(saved);
                  setShowErrors(false);
                  setSubmitError(null);
                  setReason("");
                }}
                disabled={saving}
              >
                Discard
              </Button>
              <Button onClick={handleSave} loading={saving} disabled={blockedRelaxing}>
                Save rules
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RulesTab;
